"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';

const items = [
    {
        title: 'Crawlability & Indexation',
        icon: (
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                <circle cx="14" cy="14" r="8" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: "We identify pages that search engines can't properly access or index, investigate crawl blocks and unnecessary URLs, and make sure important content is discoverable.",
    },
    {
        title: 'Site Architecture',
        icon: (
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="7" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="7" cy="25" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="25" cy="25" r="3" stroke="currentColor" strokeWidth="2" />
                <path d="M16 10v6M16 16l-9 6M16 16l9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: "We create clearer relationships between pages so search engines can understand your website's hierarchy, topics and most important content.",
    },
    {
        title: 'Core Web Vitals & Performance',
        icon: (
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                <path d="M6 20a10 10 0 1 1 20 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M16 20l5-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M4 25h24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We identify issues affecting loading, responsiveness and visual stability and prioritise improvements that contribute to a better user and search experience.',
    },
    {
        title: 'Canonicalisation & Duplicate Content',
        icon: (
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                <rect x="5" y="5" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="2" />
                <rect x="11" y="9" width="16" height="20" rx="2" fill="#ffffff" stroke="currentColor" strokeWidth="2" />
            </svg>
        ),
        desc: 'We help search engines understand which version of similar or duplicate URLs should be treated as the primary page.',
    },
    {
        title: 'Redirects & Status Codes',
        icon: (
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                <path d="M4 12h16M20 12l-4-4M20 12l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M28 20H12M12 20l4-4M12 20l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        desc: 'We identify broken links, redirect chains, incorrect status codes and other technical issues that can disrupt users and search engine crawlers.',
    },
    {
        title: 'XML Sitemaps & Robots.txt',
        icon: (
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                <rect x="6" y="4" width="20" height="24" rx="2.5" stroke="currentColor" strokeWidth="2" />
                <path d="M11 11h10M11 16h10M11 21h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We ensure your crawl directives and XML sitemap provide clear signals about which parts of your website should be discovered and indexed.',
    },
    {
        title: 'Structured Data',
        icon: (
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                <path d="M16 4l3 8 8 3-8 3-3 8-3-8-8-3 8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
        ),
        desc: 'Where relevant, we implement and validate structured data that helps search engines better understand the entities and content represented on your pages.',
    },
    {
        title: 'Internal Linking',
        icon: (
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="12" width="12" height="8" rx="4" stroke="currentColor" strokeWidth="2" />
                <rect x="16" y="12" width="12" height="8" rx="4" stroke="currentColor" strokeWidth="2" />
                <path d="M12 16h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We build logical internal connections between related pages so users and search engines can discover important content more easily.',
    },
];

function FocusRow({ item, index }: { item: typeof items[0]; index: number }) {
    const num = String(index + 1).padStart(2, '0');

    return (
        <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (index % 2) * 0.1 + Math.floor(index / 2) * 0.06 }}
            className="group relative flex items-start gap-5 py-7 px-2 -mx-2 rounded-xl transition-colors duration-300 hover:bg-[rgba(55,48,163,0.035)] border-b border-dashed last:border-b-0 md:[&:nth-last-child(-n+2)]:border-b-0"
            style={{ borderColor: 'rgba(8,4,0,0.14)' }}
        >
            {/* ghost number */}
            <span
                className="absolute right-2 top-2 select-none pointer-events-none transition-colors duration-300"
                style={{
                    fontFamily: CLASH,
                    fontSize: 44,
                    lineHeight: 1,
                    color: 'rgba(8,4,0,0.06)',
                }}
            >
                {num}
            </span>

            {/* icon */}
            <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 group-hover:-rotate-6"
                style={{
                    background: 'rgba(55,48,163,0.07)',
                    border: '1px solid rgba(55,48,163,0.16)',
                    color: PURPLE,
                }}
            >
                {item.icon}
            </div>

            {/* text */}
            <div className="flex flex-col gap-1.5 relative pr-10">
                <h3
                    className="text-lg font-bold leading-snug relative inline-block w-fit"
                    style={{ fontFamily: CLASH, color: INK, letterSpacing: '0.01em' }}
                >
                    {item.title}
                    <span
                        className="absolute left-0 -bottom-0.5 h-[3px] w-0 group-hover:w-full transition-all duration-300 ease-out"
                        style={{ background: LIME }}
                    />
                </h3>
                <p
                    className="text-sm leading-relaxed"
                    style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.01em' }}
                >
                    {item.desc}
                </p>
            </div>
        </motion.div>
    );
}

export default function TechnicalSEOWorkOn() {
    return (
        <section
            className="relative overflow-hidden py-20 md:py-28 px-6 md:px-16 lg:px-24"
            style={{ background: '#ffffff', borderRadius: '80px' }}
        >
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.035]"
                style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
            />

            <div className="max-w-[1200px] mx-auto flex flex-col gap-14 relative">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div className="flex flex-col gap-4">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="text-xs font-bold tracking-[0.2em] uppercase"
                            style={{ color: PURPLE, fontFamily: CLASH }}
                        >
                            Our Focus Areas
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                            style={{ fontFamily: CLASH, color: INK }}
                        >
                            What We Work On
                        </motion.h2>
                    </div>
                    <motion.span
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-sm font-mono"
                        style={{ color: 'rgba(8,4,0,0.35)', letterSpacing: '0.05em' }}
                    >
                        8 areas we cover
                    </motion.span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-14">
                    {items.map((it, i) => (
                        <FocusRow key={it.title} item={it} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
